import React, { useEffect, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { useHistory } from 'react-router-dom';
import moment from 'moment-timezone';

import { useCalendarState, useCalendarUI } from '../../context/CalendarContext';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { getUserTimezone } from '../../utils/timezoneHelpers';
import { useDayEvents } from '../../hooks/useDayEvents';
import { getContrastColor, getIconClass } from '../../utils/eventColorhelper';
import { adjustColor } from 'utils/colorUtils';
import { ModalHeader } from 'shared/ModalHeader';
/**
 * DayEventsPopup - Shows all events for a specific day as a popup
 * Displayed when clicking "+X more" link in month/year views
 */
const DayEventsPopup = () => {
  const { dayEventsPopup, closeDayEventsPopup, openEventViewModal } = useCalendarUI();
  const { filters, selectedUsers, caseDetails } = useCalendarState();
  const userContext = useUserDetailsContext();
  const timezone = getUserTimezone(userContext);
  const history = useHistory();

  // Fetch events for the selected day
  const { events, isLoading, isError } = useDayEvents({
    date: dayEventsPopup.date,
    timezone,
    selectedUsers,
    filters,
    case_id: caseDetails?.case_id,
    enabled: dayEventsPopup.visible && !!dayEventsPopup.date,
  });

  // Close popup on Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeDayEventsPopup();
      }
    };

    if (dayEventsPopup.visible) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [dayEventsPopup.visible, closeDayEventsPopup]);

  // Handle event click
  const handleEventClick = useCallback(
    (event) => {
      const { extendedProps } = event;
      closeDayEventsPopup();

      // Navigate to contact page for user (birthday) events
      if (extendedProps?.calendar_type === 'user') {
        history.push(`/contacts/${extendedProps.contact_id}`);
        return;
      }

      // Navigate to case page for case (statute of limitations) events
      if (extendedProps?.calendar_type === 'case') {
        history.push(`/cases/${extendedProps.case_id}`);
        return;
      }

      // Open event view modal for regular events
      openEventViewModal(event);
    },
    [closeDayEventsPopup, openEventViewModal, history]
  );

  // Format time for display
  const formatEventTime = useCallback(
    (event) => {
      if (event.allDay) {
        return 'All Day';
      }

      const start = moment.tz(event.start, timezone);
      const end = moment.tz(event.end, timezone);

      return `${start.format('h:mm A')} - ${end.format('h:mm A')}`;
    },
    [timezone]
  );

  // Get event title based on type
  const getEventTitle = useCallback((event) => {
    const { extendedProps } = event;

    if (extendedProps?.calendar_type === 'case') {
      return 'Statute Of Limitations';
    }
    if (extendedProps?.calendar_type === 'user') {
      return `Birthday: ${extendedProps.contact_name || 'Unknown'}`;
    }

    return event.title || 'Untitled Event';
  }, []);

  if (!dayEventsPopup.visible) {
    return null;
  }

  const formattedDate = dayEventsPopup.date ? moment.tz(dayEventsPopup.date, timezone).format('dddd, D MMM YYYY') : '';

  return (
    <Dialog
      visible={dayEventsPopup.visible}
      onHide={closeDayEventsPopup}
      header={ModalHeader}
      style={{ width: '320px', maxHeight: '80vh' }}
      className="new-task-popup media-popup day-events-popup-dialog"
      footer={<></>}
      resizable={false}
      modal
      closeOnEscape
      closeOnBackdropClick
    >
      {/* Modal Heading */}
      <div className="d-flex justify-content-between align-items-center mt-2 mb-2">
        <div className="modal-heading F-size16 text-bold">
          <i className="pi pi-calendar me-2" style={{ color: 'var(--primary-main)' }}></i>
          {formattedDate}
        </div>
      </div>

      {/* Content */}
      <div className="day-events-popup-content">
        {isLoading && (
          <div className="day-events-popup-loading">
            <i className="pi pi-spin pi-spinner"></i>
            <span>Loading events...</span>
          </div>
        )}

        {isError && (
          <div className="day-events-popup-error">
            <i className="pi pi-exclamation-triangle"></i>
            <span>Failed to load events</span>
          </div>
        )}

        {!isLoading && !isError && events.length === 0 && (
          <div className="day-events-popup-empty">
            <i className="pi pi-calendar-times"></i>
            <span>No events for this day</span>
          </div>
        )}

        {!isLoading && !isError && events.length > 0 && (
          <div className="day-events-popup-list">
            {events.map((event) => {
              const bgColor = event.backgroundColor || '#3b82f6';
              const textColor = getContrastColor(bgColor);
              const bgColor2 = adjustColor(bgColor, 40);
              const calendarType = event.extendedProps?.calendar_type || 'event';
              const iconClass = getIconClass(calendarType, event.extendedProps?.is_google_event);

              return (
                <div
                  key={event.id}
                  className="day-events-popup-item minimalist-event-card"
                  style={{ backgroundColor: bgColor }}
                  onClick={() => handleEventClick(event)}
                >
                  {/* Left accent border */}
                  <div className="event-accent" style={{ backgroundColor: textColor, opacity: 0.3 }}></div>

                  {/* Event content */}
                  <div className="event-content-wrapper">
                    {/* Time */}
                    <div className="event-time" style={{ color: textColor, opacity: 0.9 }}>
                      <i
                        className={`${iconClass} event-type-icon`}
                        style={{ color: textColor, opacity: 0.9, backgroundColor: bgColor2 }}
                        title={calendarType}
                      ></i>
                      {formatEventTime(event)}
                    </div>

                    {/* Title */}
                    <div className="event-title-row">
                      <div className="event-title" title={getEventTitle(event)} style={{ color: textColor }}>
                        {getEventTitle(event)}
                      </div>
                    </div>

                    {/* Subtitle (case name) */}
                    {event.extendedProps?.case_name && (
                      <div className="event-subtitle" title={event.extendedProps.case_name} style={{ color: textColor, opacity: 0.7 }}>
                        {event.extendedProps.case_name}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer with event count */}
      {!isLoading && !isError && events.length > 0 && (
        <div className="pt-1">
          <span className="text-muted F-size12">
            {events.length} event{events.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}
    </Dialog>
  );
};

export default DayEventsPopup;
